// frontend/src/context/AuthContext.jsx
// SignalIQ Auth Context — production-safe authentication + enterprise RBAC
//
// Production hardening:
// - No guest/free/viewer fallback session is created.
// - If no valid access token exists, user is null and routes must redirect to /login.
// - Registration does not automatically authenticate an unverified account.
// - Email verification defaults to unverified when verification is required.
// - Owner/admin/tester RBAC remains preserved.
// - Testers retain product-module beta access but are blocked from admin/platform/governance/model tools.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import api, { setToken, clearToken, getToken, getApiError } from "../api/client";
import {
  login as apiLogin,
  register as apiRegister,
  me as apiMe,
} from "../api/auth";

const AuthContext = createContext(null);

const REFRESH_TOKEN_KEY = "signaliq_refresh_token";
const REFRESH_TOKEN_EXPIRES_KEY = "signaliq_refresh_token_expires_at";

const EMAIL_VERIFICATION_REQUIRED =
  String(import.meta.env?.VITE_EMAIL_VERIFICATION_REQUIRED || "").toLowerCase() === "true";

const ADMIN_DEV_BYPASS =
  String(import.meta.env?.VITE_ADMIN_DEV_BYPASS || "").toLowerCase() === "true";

const OWNER_EMAIL = String(
  import.meta.env?.VITE_OWNER_EMAIL || "daniel@signaliqintelligence.com"
)
  .trim()
  .toLowerCase();

const ADMIN_ROLES = new Set(["owner", "admin"]);
const GOVERNANCE_ROLES = new Set(["owner", "admin"]);
const PRODUCT_BETA_ROLES = new Set(["owner", "admin", "tester"]);

function normalizeRole(role) {
  const raw = String(role || "").trim().toLowerCase();

  const aliases = {
    superuser: "owner",
    super_admin: "owner",
    superadmin: "owner",
    administrator: "admin",
    team_admin: "admin",
    manager: "admin",
    member: "analyst",
    user: "analyst",
    billing: "billing_admin",
    billingadmin: "billing_admin",
    beta: "tester",
    beta_tester: "tester",
    test_user: "tester",
  };

  const normalized = aliases[raw] || raw;

  if (
    [
      "owner",
      "admin",
      "billing_admin",
      "reviewer",
      "analyst",
      "tester",
      "viewer",
      "client",
    ].includes(normalized)
  ) {
    return normalized;
  }

  return "viewer";
}

function normalizeTier(tier) {
  const raw = String(tier || "").trim().toLowerCase();

  const aliases = {
    pro: "professional",
    paid: "professional",
    business: "team",
    corp: "enterprise",
    enterprise_plus: "institutional",
    institutional_plan: "institutional",
    enterprise_plan: "enterprise",
    beta: "institutional",
    tester: "institutional",
  };

  const normalized = aliases[raw] || raw;

  if (
    [
      "free",
      "starter",
      "professional",
      "team",
      "enterprise",
      "institutional",
    ].includes(normalized)
  ) {
    return normalized;
  }

  return "free";
}

function normalizeBoolean(value, fallback = false) {
  if (typeof value === "boolean") return value;
  if (value === 1 || value === "1" || value === "true") return true;
  if (value === 0 || value === "0" || value === "false") return false;
  return fallback;
}

function normalizeOnboardingStatus(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (["not_started", "in_progress", "completed", "dismissed"].includes(raw)) {
    return raw;
  }
  return "not_started";
}

function hasUsableIdentity(data = {}) {
  return Boolean(
    data &&
      (
        data.id !== undefined ||
        data.user_id !== undefined ||
        String(data.email || "").trim()
      )
  );
}

function normalizeUser(data = {}) {
  if (!hasUsableIdentity(data)) return null;

  const normalizedEmail = String(data.email || "").trim().toLowerCase();

  const rawRole = normalizeRole(
    data.access_role ??
      data.role ??
      data.user_role ??
      data.userRole ??
      ""
  );

  const computedOwner =
    rawRole === "owner" ||
    normalizedEmail === OWNER_EMAIL;

  const role = computedOwner ? "owner" : rawRole;

  const rawTier = normalizeTier(
    data.tier ??
      data.plan ??
      data.subscription_tier ??
      data.subscriptionTier ??
      data.subscription?.tier ??
      data.organization?.tier ??
      data.organisation?.tier ??
      ""
  );

  const tier = PRODUCT_BETA_ROLES.has(role) ? "institutional" : rawTier;

  const isOwner = normalizeBoolean(data.is_owner, computedOwner);
  const isAdmin = normalizeBoolean(
    data.is_admin,
    role === "admin" || computedOwner
  );
  const isBillingAdmin = normalizeBoolean(
    data.is_billing_admin,
    role === "billing_admin"
  );
  const isTester = normalizeBoolean(
    data.is_tester,
    role === "tester"
  );

  const verificationRequired = normalizeBoolean(
    data.email_verification_required ?? data.emailVerificationRequired,
    EMAIL_VERIFICATION_REQUIRED && !ADMIN_DEV_BYPASS
  );

  const emailVerified = normalizeBoolean(
    data.email_verified ?? data.emailVerified,
    verificationRequired ? false : true
  );

  return {
    id: data.id ?? data.user_id ?? null,
    email: normalizedEmail,
    full_name: String(data.full_name || data.fullName || "").trim(),
    name: String(data.full_name || data.fullName || data.name || "").trim(),
    role,
    tier,
    org_id: data.org_id ?? data.orgId ?? data.organization_id ?? null,
    organization_id: data.organization_id ?? data.org_id ?? data.orgId ?? null,
    is_owner: isOwner,
    is_admin: isAdmin,
    is_billing_admin: isBillingAdmin,
    is_tester: isTester,
    email_verified: emailVerified,
    email_verification_required: verificationRequired,
    mfa_enabled: normalizeBoolean(data.mfa_enabled ?? data.mfaEnabled),
    onboarding_status: normalizeOnboardingStatus(
      data.onboarding_status ?? data.onboardingStatus
    ),
    onboarding_completed_at:
      data.onboarding_completed_at ?? data.onboardingCompletedAt ?? null,
    onboarding_context:
      (typeof data.onboarding_context === "object" && data.onboarding_context) ||
      (typeof data.onboardingContext === "object" && data.onboardingContext) ||
      {},
    _raw: data,
  };
}

function storeRefreshToken(token, expiresAt) {
  if (!token) return;
  try {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
    if (expiresAt) {
      localStorage.setItem(REFRESH_TOKEN_EXPIRES_KEY, expiresAt);
    }
  } catch {
    // storage may be blocked
  }
}

function loadRefreshToken() {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY) || null;
  } catch {
    return null;
  }
}

function clearRefreshToken() {
  try {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_EXPIRES_KEY);
  } catch {
    // ignore
  }
}

function clearAuthStorage() {
  clearToken();
  clearRefreshToken();

  try {
    localStorage.removeItem("token");
    localStorage.removeItem("access_token");
    localStorage.removeItem("auth_token");
    localStorage.removeItem("signaliq_token");
    localStorage.removeItem("signaliq_access_token");
    localStorage.removeItem("user");
    localStorage.removeItem("signaliq_user");
  } catch {
    // ignore
  }

  try {
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("access_token");
    sessionStorage.removeItem("auth_token");
    sessionStorage.removeItem("signaliq_token");
    sessionStorage.removeItem("signaliq_access_token");
    sessionStorage.removeItem("user");
    sessionStorage.removeItem("signaliq_user");
  } catch {
    // ignore
  }
}

function isVerifiedEnough(user) {
  if (!user) return false;
  if (!user.email_verification_required) return true;
  return Boolean(user.email_verified);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionRestored, setSessionRestored] = useState(false);

  const refreshAccessToken = useCallback(async () => {
    const refreshToken = loadRefreshToken();
    if (!refreshToken) throw new Error("No refresh token");

    const data = await api.post(
      "/auth/refresh",
      { refresh_token: refreshToken },
      { credentials: "include", clearTokenOn401: true }
    );

    if (!data?.access_token) {
      throw new Error("Refresh did not return an access token");
    }

    setToken(data.access_token);

    if (data.refresh_token) {
      storeRefreshToken(data.refresh_token, data.refresh_token_expires_at);
    }

    return data;
  }, []);

  const refreshMe = useCallback(async () => {
    try {
      const data = await apiMe();
      const normalized = normalizeUser(data);

      if (!normalized || !isVerifiedEnough(normalized)) {
        clearAuthStorage();
        setUser(null);
        return null;
      }

      setUser(normalized);
      return normalized;
    } catch {
      clearAuthStorage();
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const restore = async () => {
      const applyUser = (data) => {
        const normalized = normalizeUser(data);

        if (!normalized || !isVerifiedEnough(normalized)) {
          return null;
        }

        if (!cancelled) setUser(normalized);
        return normalized;
      };

      const finishSignedOut = () => {
        clearAuthStorage();
        if (!cancelled) setUser(null);
      };

      try {
        /*
          Cookie-first session restore:
          Some production auth flows use an httpOnly browser cookie instead of
          returning an access token to localStorage. In that case
          localStorage.signaliq_token can be null while /auth/me is still valid.
          Always try /auth/me first before clearing storage or redirecting.
        */
        try {
          const data = await apiMe();
          const restoredUser = applyUser(data);
          if (restoredUser) return;
        } catch {
          // Continue to token and refresh-token recovery.
        }

        const token = getToken();

        if (token) {
          try {
            const data = await apiMe();
            const restoredUser = applyUser(data);
            if (restoredUser) return;
          } catch {
            // Stored access token may be stale. Try refresh below.
          }
        }

        try {
          await refreshAccessToken();
          const data = await apiMe();
          const restoredUser = applyUser(data);
          if (restoredUser) return;
        } catch {
          finishSignedOut();
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setSessionRestored(true);
        }
      }
    };

    restore();

    return () => {
      cancelled = true;
    };
  }, [refreshAccessToken]);

  const login = useCallback(async (email, password, mfaCode, extra = {}) => {
    try {
      const data = await apiLogin(email, password, mfaCode, extra);

      if (data.access_token) setToken(data.access_token);
      if (data.refresh_token) {
        storeRefreshToken(data.refresh_token, data.refresh_token_expires_at);
      }

      let normalized = normalizeUser(data);

      if (!normalized || !normalized.email) {
        try {
          const me = await apiMe();
          normalized = normalizeUser(me);
        } catch {
          // handled below
        }
      }

      if (!normalized) {
        clearAuthStorage();
        throw new Error("Login failed — server did not return a valid user session.");
      }

      if (!isVerifiedEnough(normalized)) {
        clearAuthStorage();
        setUser(null);

        return {
          ...data,
          requires_email_verification: true,
          email_verification_required: true,
        };
      }

      setUser(normalized);
      return data;
    } catch (error) {
      clearAuthStorage();
      setUser(null);

      const detail =
        error?.response?.data?.detail ||
        error?.detail ||
        {};

      if (
        detail?.code === "EMAIL_NOT_VERIFIED" ||
        String(detail?.message || "").toLowerCase().includes("verify") ||
        String(error?.message || "").toLowerCase().includes("verify")
      ) {
        const verificationError = new Error(
          detail?.message || error?.message || "Please verify your email before signing in."
        );
        verificationError.detail = detail;
        throw verificationError;
      }

      throw new Error(
        getApiError(error, "Login failed — check email and password.")
      );
    }
  }, []);

  const register = useCallback(async (payload) => {
    try {
      const data = await apiRegister(payload);
      const normalized = normalizeUser(data);

      const registrationRequiresVerification =
        Boolean(data?.requires_email_verification) ||
        Boolean(data?.email_verification_required) ||
        EMAIL_VERIFICATION_REQUIRED;

      if (registrationRequiresVerification && (!normalized || !normalized.email_verified)) {
        clearAuthStorage();
        setUser(null);

        return {
          ...data,
          requires_email_verification: true,
          email_verification_required: true,
        };
      }

      if (data.access_token) setToken(data.access_token);
      if (data.refresh_token) {
        storeRefreshToken(data.refresh_token, data.refresh_token_expires_at);
      }

      let verifiedUser = normalized;

      if (!verifiedUser || !verifiedUser.email) {
        try {
          const me = await apiMe();
          verifiedUser = normalizeUser(me);
        } catch {
          // keep null
        }
      }

      if (!verifiedUser || !isVerifiedEnough(verifiedUser)) {
        clearAuthStorage();
        setUser(null);

        return {
          ...data,
          requires_email_verification: true,
          email_verification_required: true,
        };
      }

      setUser(verifiedUser);
      return data;
    } catch (error) {
      clearAuthStorage();
      setUser(null);
      throw new Error(getApiError(error, "Registration failed."));
    }
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = loadRefreshToken();

    try {
      await api.post(
        "/auth/logout",
        { refresh_token: refreshToken },
        { credentials: "include" }
      );
    } catch {
      // ignore logout API errors
    }

    clearAuthStorage();
    setUser(null);
  }, []);

  const resendVerification = useCallback(async () => {
    return api.post(
      "/auth/resend-verification",
      {},
      { credentials: "include" }
    );
  }, []);

  const markOnboardingComplete = useCallback(
    async (context = {}) => {
      if (!user) return null;

      const nextUser = normalizeUser({
        ...(user || {}),
        onboarding_status: "completed",
        onboarding_completed_at: new Date().toISOString(),
        onboarding_context: {
          ...(user?.onboarding_context || {}),
          ...context,
        },
      });

      setUser(nextUser);
      return nextUser;
    },
    [user]
  );

  const canAccessAdmin = useCallback(() => {
    if (!user) return false;

    const role = String(user.role || "").toLowerCase();
    if (role === "tester") return false;

    return Boolean(
      user.is_owner ||
        user.is_admin ||
        ADMIN_ROLES.has(role)
    );
  }, [user]);

  const canAccessPlatform = useCallback(() => {
    return canAccessAdmin();
  }, [canAccessAdmin]);

  const canAccessGovernance = useCallback(() => {
    if (!user) return false;

    const role = String(user.role || "").toLowerCase();
    if (role === "tester") return false;

    return Boolean(
      user.is_owner ||
        user.is_admin ||
        GOVERNANCE_ROLES.has(role)
    );
  }, [user]);

  const canAccessModelLab = useCallback(() => {
    return canAccessGovernance();
  }, [canAccessGovernance]);

  const canAccessProductModules = useCallback(() => {
    if (!user) return false;

    const role = String(user.role || "").toLowerCase();
    const tier = String(user.tier || "").toLowerCase();

    return Boolean(
      PRODUCT_BETA_ROLES.has(role) ||
        ["billing_admin", "reviewer", "analyst"].includes(role) ||
        ["institutional", "enterprise", "team", "professional"].includes(tier)
    );
  }, [user]);

  const isTesterAccount = useCallback(() => {
    if (!user) return false;

    return Boolean(
      user.is_tester ||
        String(user.role || "").toLowerCase() === "tester"
    );
  }, [user]);

  const isAuthenticated = Boolean(user && isVerifiedEnough(user));

  const value = useMemo(
    () => ({
      user,
      loading,
      sessionRestored,
      isAuthenticated,
      needsOnboarding:
        Boolean(user) &&
        ["not_started", "in_progress"].includes(
          String(user?.onboarding_status || "not_started").toLowerCase()
        ),

      login,
      register,
      logout,
      refreshMe,
      refreshAccessToken,
      resendVerification,
      markOnboardingComplete,

      canAccessAdmin,
      canAccessPlatform,
      canAccessGovernance,
      canAccessModelLab,
      canAccessProductModules,
      isTesterAccount,

      emailVerificationRequired:
        EMAIL_VERIFICATION_REQUIRED && !ADMIN_DEV_BYPASS,
    }),
    [
      user,
      loading,
      sessionRestored,
      isAuthenticated,
      login,
      register,
      logout,
      refreshMe,
      refreshAccessToken,
      resendVerification,
      markOnboardingComplete,
      canAccessAdmin,
      canAccessPlatform,
      canAccessGovernance,
      canAccessModelLab,
      canAccessProductModules,
      isTesterAccount,
    ]
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return context;
}
